import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { MonthlyPoint } from '../../api/types';

interface Props {
  points: MonthlyPoint[];
  loading?: boolean;
  onMonthClick?: (month: string) => void;
}

// Canonical model order (most expensive first → bottom of stack).
// Unlisted models (e.g. retired ones still in historical data) fall back to
// the generic label/color below rather than being dropped from the chart.
const MODEL_ORDER = ['claude-opus-4-6', 'claude-sonnet-5', 'claude-haiku-4-5'] as const;

const MODEL_LABELS: Record<string, string> = {
  'claude-opus-4-6': 'Opus 4.6',
  'claude-sonnet-5': 'Sonnet 5',
  'claude-haiku-4-5': 'Haiku 4.5',
};

const MODEL_COLORS: Record<string, string> = {
  'claude-opus-4-6': '#7c3aed',     // violet 600
  'claude-sonnet-5': '#4f46e5',     // indigo 600
  'claude-haiku-4-5': '#0ea5e9',    // sky 500
};

const UNKNOWN_COLOR = '#a1a1aa'; // zinc 400

function formatUsd(n: number): string {
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

export function MonthlyCostByModelChart({ points, loading, onMonthClick }: Props): JSX.Element {
  const months = points.map((p) => p.month);

  const knownModels = new Set(MODEL_ORDER);
  const extraModels = new Set<string>();
  for (const p of points) {
    for (const key of Object.keys(p.costByModel)) {
      if (!knownModels.has(key as (typeof MODEL_ORDER)[number])) extraModels.add(key);
    }
  }
  const orderedModels: string[] = [...MODEL_ORDER, ...[...extraModels].sort()];

  const option: EChartsOption = {
    grid: { left: 64, right: 24, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (raw) => formatUsd(Number(raw ?? 0)),
    },
    xAxis: {
      type: 'category',
      data: months,
    },
    yAxis: {
      type: 'value',
      name: 'Cost (USD)',
      nameLocation: 'middle',
      nameGap: 52,
      axisLabel: { formatter: (v: number) => formatUsd(v) },
    },
    series: orderedModels.map((model) => ({
      name: MODEL_LABELS[model] ?? model,
      type: 'bar',
      stack: 'cost',
      barMaxWidth: 40,
      itemStyle: { color: MODEL_COLORS[model] ?? UNKNOWN_COLOR },
      data: points.map((p) => Number((p.costByModel[model] ?? 0).toFixed(2))),
    })),
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: '100%', cursor: onMonthClick ? 'pointer' : 'default' }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={
        onMonthClick
          ? {
              click: (params: { name?: string }) => {
                if (params?.name) onMonthClick(params.name);
              },
            }
          : undefined
      }
    />
  );
}
